import { DatePicker, TimePicker } from "design-system/molecules/DatePicker"
import { startOfDay } from "lopermut"
import {
  combineDateAndTime,
  formatLaunchTime,
  isUpcoming,
  toTimeInputValue,
} from "modules/campaigns/lib/launchSchedule"
import { useState } from "react"
import { useTranslation } from "react-i18next"

type Props = {
  id: string
  defaultValue?: Date
  onChange: (scheduledAt: Date | undefined) => void
  disabled?: boolean
}

export function LaunchScheduleInput({ id, defaultValue, onChange, disabled }: Props) {
  const { t, i18n } = useTranslation()
  const [date, setDate] = useState(defaultValue)
  const [time, setTime] = useState(defaultValue ? toTimeInputValue(defaultValue) : "")

  const scheduledAt = combineDateAndTime(date, time)
  const upcomingScheduledAt = scheduledAt && isUpcoming(scheduledAt) ? scheduledAt : undefined
  const isInPast = Boolean(scheduledAt) && !upcomingScheduledAt
  const errorId = `${id}-error`

  function handleDateChange(newDate: Date | undefined) {
    setDate(newDate)
    onChange(combineDateAndTime(newDate, time))
  }

  function handleTimeChange(newTime: string) {
    setTime(newTime)
    onChange(combineDateAndTime(date, newTime))
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <DatePicker
          date={date}
          onDateChange={handleDateChange}
          placeholder={t("campaigns.launchManual.scheduleDatePlaceholder")}
          fromDate={startOfDay(new Date())}
          disabled={disabled}
        />
        <TimePicker
          id={id}
          time={time}
          onTimeChange={handleTimeChange}
          aria-label={t("campaigns.launchManual.scheduleTime")}
          aria-invalid={isInPast}
          aria-describedby={isInPast ? errorId : undefined}
          disabled={disabled}
        />
      </div>
      <p className="text-sm text-muted-foreground">
        {t("campaigns.launchManual.scheduleTimezone", {
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        })}
      </p>
      {upcomingScheduledAt && (
        <p className="text-sm">
          {t("campaigns.launchManual.scheduleSummary", {
            date: formatLaunchTime(upcomingScheduledAt, i18n.language),
          })}
        </p>
      )}
      {isInPast && (
        <p id={errorId} role="alert" className="text-sm text-destructive">
          {t("campaigns.launchManual.scheduleInPast")}
        </p>
      )}
    </div>
  )
}
